/**
 * Гейт заблокированного агента на фронте (DEV-2087).
 *
 * Блокировка живёт в agents-service как agent_status = blocked. Кабинет узнаёт
 * о ней двумя путями, и оба нужны:
 *
 * 1) ГЛАВНЫЙ — статус в профиле. AgentLayout при монтировании читает
 *    GetMyProfile (метод намеренно открыт заблокированному) и по agentStatus
 *    уводит на экран отказа. Это закрывает и разделы, которые в agents вообще
 *    не ходят: /projects читает только properties, и 403 оттуда не придёт.
 * 2) ВТОРОЙ РУБЕЖ — 403 от agents-service. Сессия живёт долго, профиль мог быть
 *    прочитан ещё до блокировки; тогда первый же кабинетный запрос вернёт
 *    PermissionDenied → HTTP 403, и уводим по нему.
 *
 * ⚠️ Сессия НЕ гасится и токены не чистятся. 403 — это не протухший токен (им
 * занимается auth.interceptor со своим refresh), а закрытый доступ. Разлогин
 * выглядел бы как «сломался вход», и человек вводил бы тот же пароль по кругу —
 * ровно та ошибка, что чинили в DEV-1265.
 */
import type { AxiosError, AxiosInstance } from 'axios'
import { tokenStorage } from '@/features/auth/tokenStorage'

/** Метка в query экрана отказа: показать текст про блокировку, а не про пустую карту. */
export const BLOCKED_REASON = 'blocked'

/** agent_status заблокированного агента (agents-service, DEV-2087). */
export const BLOCKED_AGENT_STATUS = 'blocked'

/**
 * Текст ошибки блокировки из agents-service
 * (internal/server/grpc/blocked_agent.go: status.Error(PermissionDenied, ...)).
 * gateway кладёт его в тело ответа полем message.
 *
 * ⚠️ Сверяем именно текст. 403 от agents прилетает и по другим причинам —
 * например requireAgent на токене без роли AGENT отвечает «agent role required».
 * Показать такому человеку «вас заблокировали» значит отправить его оспаривать
 * несуществующую блокировку.
 */
const BLOCKED_MESSAGE = 'agent is blocked'

/**
 * Гейтим только 403 от собственного сервиса кабинета. 403 от properties, deals
 * и прочих — про конкретное право на конкретные данные, и выкидывать из-за него
 * человека из кабинета нельзя.
 */
const AGENTS_API_PREFIX = '/api/v1/agents/'

/**
 * Маршруты, с которых НЕ уводим.
 *
 * Экран отказа — чтобы не пушить его самого по кругу. Разделы поддержки —
 * решение DEV-2087: блокировка закрывает кабинет и бота, но обращения остаются,
 * человек должен иметь возможность её оспорить. Отправить его отсюда на экран
 * отказа значило бы закрыть единственный оставшийся канал.
 *
 * Имена сверяются с роутером в src/router/__tests__/routeCoverage.spec.ts:
 * переименование раздела иначе молча закрыло бы канал апелляции.
 */
export const KEEP_ON_ROUTES = new Set([
  'agent-no-access',
  'agent-support',
  'agent-inquiries',
  'agent-inquiry-detail',
  'agent-other',
])

/** Маршрут, с которого заблокированного не уводим (см. KEEP_ON_ROUTES). */
export const isKeptWhenBlocked = (currentRouteName: unknown): boolean =>
  typeof currentRouteName === 'string' && KEEP_ON_ROUTES.has(currentRouteName)

/** Куда уводить заблокированного с текущего маршрута; null — оставить на месте. */
export const blockedRedirect = (agentStatus: string | undefined, currentRouteName: unknown) =>
  agentStatus === BLOCKED_AGENT_STATUS && !isKeptWhenBlocked(currentRouteName)
    ? { name: 'agent-no-access', query: { reason: BLOCKED_REASON } }
    : null

/** message из тела ответа gateway ('' — тела нет или оно не в этой форме). */
const responseMessage = (error: AxiosError): string => {
  const message = (error.response?.data as { message?: unknown } | undefined)?.message
  return typeof message === 'string' ? message : ''
}

/**
 * Решение «это закрытый кабинет, а не частный отказ». Отдельная чистая функция:
 * вся логика проверяется тестом без роутера и без сети.
 */
export const isCabinetForbidden = (error: AxiosError, currentRouteName: unknown): boolean => {
  if (error.response?.status !== 403) {
    return false
  }

  if (responseMessage(error) !== BLOCKED_MESSAGE) {
    return false
  }

  // url приходит и относительным, и с baseURL (сгенерированные клиенты клеят
  // basePath сами), поэтому ищем префикс внутри строки, а не в начале.
  const path = (error.config?.url ?? '').split('?')[0] ?? ''
  if (!path.includes(AGENTS_API_PREFIX)) {
    return false
  }

  // Аноним на форме входа/регистрации: блокировать там ещё некого.
  if (!tokenStorage.getAccessToken()) {
    return false
  }

  return !isKeptWhenBlocked(currentRouteName)
}

/**
 * Роутер берётся динамическим импортом: он тянет сторы, а те — этот же модуль
 * через @/api, так что статическая зависимость была бы циклом. Промис кешируем:
 * шквал 403 иначе дёргает импорт на каждый ответ.
 */
let routerModule: Promise<typeof import('@/router')> | null = null
const getRouter = async () => (routerModule ??= import('@/router')).then((m) => m.default)

export function setupAccessInterceptor(axiosInstance: AxiosInstance) {
  // Шквал 403 при загрузке страницы не должен плодить навигации.
  let isRedirecting = false

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error.response?.status === 403) {
        try {
          const router = await getRouter()

          // Замок берём ТОЛЬКО после решения. Захват до проверки держал бы его
          // на время импорта из-за чужого 403 и проглатывал кабинетный,
          // прилетевший в это же окно — редиректа тогда не будет вовсе.
          if (isCabinetForbidden(error, router.currentRoute.value.name) && !isRedirecting) {
            isRedirecting = true

            try {
              await router.push({ name: 'agent-no-access', query: { reason: BLOCKED_REASON } })
            } finally {
              isRedirecting = false
            }
          }
        } catch {
          /* навигация отменена или роутер ещё не поднят — ошибку всё равно отдаём наверх */
        }
      }

      return Promise.reject(error)
    },
  )
}
